export type CalendarProvider = 'google' | 'icloud' | 'radicale' | 'chronos';
export type CalendarAccountStatus = 'ok' | 'error' | 'needs_reauth';

export interface CalendarSummary {
  id: number;
  account_id: number;
  /** Display label: custom_name when set, otherwise the imported name. */
  name: string;
  synced_name: string;
  custom_name: string | null;
  color: string | null;
  timezone: string | null;
  selected: boolean;
  is_busy_source: boolean;
  is_write_target: boolean;
  read_only: boolean;
  /** Client every event from this calendar gets, unless set per-event. */
  default_client_id: number | null;
  default_project_id: number | null;
  default_billable: boolean;
  /** Default length of an "Add prep time" task, in minutes. */
  prep_minutes: number;
  last_synced_at: string | null;
}

/** Local display label for a calendar row in Settings or Plan. */
export function calendarDisplayName(
  cal: Pick<CalendarSummary, 'name' | 'custom_name' | 'synced_name'>,
): string {
  const custom = cal.custom_name?.trim();
  if (custom) return custom;
  return cal.synced_name?.trim() || cal.name;
}

export interface CalendarAccount {
  id: number;
  provider: CalendarProvider;
  label: string;
  status: CalendarAccountStatus;
  last_error: string | null;
  last_synced_at: string | null;
  caldav_url: string | null;
  calendars: CalendarSummary[];
}

export interface CalendarStatus {
  google_configured: boolean;
  google_redirect_uri: string;
}

export interface CalDavConnectInput {
  provider: 'icloud' | 'radicale';
  username: string;
  password: string;
  url?: string;
  label?: string;
}

export interface CalendarUpdate {
  calendarId: number;
  selected?: boolean;
  is_busy_source?: boolean;
  is_write_target?: boolean;
  color?: string;
  custom_name?: string | null;
  default_client_id?: number | null;
  default_project_id?: number | null;
  default_billable?: boolean;
  prep_minutes?: number;
}

/* Time blocks */

export interface TimeBlock {
  id: number;
  task_id: number;
  starts_at: string;
  ends_at: string;
  status: 'proposed' | 'confirmed' | 'completed' | 'skipped';
  origin: 'manual_drag' | 'auto_scheduled' | 'ai_suggested';
  remote_uid: string | null;
  write_error: string | null;
  is_continuation: boolean;
  split_group_id: string | null;
  task_title: string;
  client_id: number;
  client_color: string;
  estimate_minutes: number | null;
}

export interface BusyInterval {
  starts_at: string;
  ends_at: string;
}

export interface CreateTimeBlockInput {
  task_id: number;
  starts_at: string;
  ends_at?: string;
  status?: TimeBlock['status'];
  origin?: TimeBlock['origin'];
  is_continuation?: boolean;
  split_group_id?: string | null;
}

export interface UpdateTimeBlockInput {
  starts_at?: string;
  ends_at?: string;
  status?: TimeBlock['status'];
  is_continuation?: boolean;
  split_group_id?: string | null;
  clear_split_group?: boolean;
}

/* Blockers */

export type BlockerKind = 'lunch' | 'break' | 'commute' | 'personal' | 'other';

/**
 * Time the user is unavailable but not working.
 *
 * Busy for the scheduler, invisible to the day's assignment count, a day with
 * lunch and a commute on it is not a day with two things to do.
 */
export interface Blocker {
  id: number;
  kind: BlockerKind;
  label: string | null;
  starts_at: string;
  ends_at: string;
}

export interface CreateBlockerInput {
  kind: BlockerKind;
  label?: string | null;
  starts_at: string;
  ends_at?: string;
}

export interface UpdateBlockerInput {
  kind?: BlockerKind;
  label?: string | null;
  starts_at?: string;
  ends_at?: string;
}

/** Kind → display name and the duration a fresh drag lands at. Mirrors the API. */
export const BLOCKER_KINDS: {
  value: BlockerKind;
  label: string;
  /** Palette wording, when the drag source reads better than the placed name. */
  paletteLabel?: string;
  minutes: number;
}[] = [
  { value: 'lunch', label: 'Lunch', minutes: 60 },
  { value: 'break', label: 'Break', minutes: 15 },
  { value: 'commute', label: 'Commute', minutes: 30 },
  { value: 'personal', label: 'Personal', minutes: 30 },
  { value: 'other', label: 'Blocker', paletteLabel: 'Custom…', minutes: 30 },
];

export function blockerTitle(blocker: Pick<Blocker, 'kind' | 'label'>): string {
  if (blocker.label) return blocker.label;
  return BLOCKER_KINDS.find((k) => k.value === blocker.kind)?.label ?? 'Blocker';
}
